/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface DeclineSecondaryReasonProps {
  id: string;
  name: string;
  declineReasonId: string;
  cancellationReasonId: string | null;
  cancellationSecondaryReasonId: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class DeclineSecondaryReasonRecord extends Record<DeclineSecondaryReasonProps>({
  id: null as any,
  name: null as any,
  declineReasonId: null as any,
  cancellationReasonId: null as any,
  cancellationSecondaryReasonId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    declineReasonId: string;
    cancellationReasonId: string | null;
    cancellationSecondaryReasonId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): DeclineSecondaryReasonRecord {
    if (json == null) {
      throw new Error('DeclineSecondaryReasonRecord.fromJS: parameter cannot be null');
    }
    return new DeclineSecondaryReasonRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'DeclineSecondaryReasonJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'DeclineSecondaryReasonJson.name'),
      declineReasonId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'declineReasonId', '00000000-0000-0000-0000-000000000000'),
        'DeclineSecondaryReasonJson.declineReasonId',
      ),
      cancellationReasonId: json.cancellationReasonId,
      cancellationSecondaryReasonId: json.cancellationSecondaryReasonId,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    declineReasonId: string;
    cancellationReasonId: string | null;
    cancellationSecondaryReasonId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [DeclineSecondaryReasonRecord, Error[]] {
    if (json == null) {
      throw new Error('DeclineSecondaryReasonRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new DeclineSecondaryReasonRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'DeclineSecondaryReasonJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'DeclineSecondaryReasonJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      declineReasonId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'declineReasonId', '00000000-0000-0000-0000-000000000000'),
            'DeclineSecondaryReasonJson.declineReasonId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cancellationReasonId: tryOrDefault(
        () => json.cancellationReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cancellationSecondaryReasonId: tryOrDefault(
        () => json.cancellationSecondaryReasonId,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: DeclineSecondaryReasonProps) {
    super(props);
    if (props == null) {
      throw new Error('DeclineSecondaryReasonRecord constructor parameter cannot be null');
    }
  }
}

export abstract class DeclineSecondaryReasonViewModelBase implements DeclineSecondaryReasonProps {
  protected readonly _props: DeclineSecondaryReasonProps;
  private _json?: DeclineSecondaryReasonProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get declineReasonId(): string {
    return this._props.declineReasonId;
  }
  get cancellationReasonId(): string | null {
    return this._props.cancellationReasonId;
  }
  get cancellationSecondaryReasonId(): string | null {
    return this._props.cancellationSecondaryReasonId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: DeclineSecondaryReasonProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        declineReasonId: this._props.declineReasonId,
        cancellationReasonId: this._props.cancellationReasonId,
        cancellationSecondaryReasonId: this._props.cancellationSecondaryReasonId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllDeclineSecondaryReasonEntities = (state: AppState) =>
  state.entityStores.declineSecondaryReason.entities;

export const selectDeclineSecondaryReasonById = (id: string) => (state: AppState) =>
  state.entityStores.declineSecondaryReason.entities.get(id);

export const selectDeclineSecondaryReasonMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.declineSecondaryReason.meta.get(id);

export const selectDeclineSecondaryReasonAndMetadataById = (id: string) =>
  createSelector(
    selectDeclineSecondaryReasonById(id),
    selectDeclineSecondaryReasonMetadataById(id),
    (declineSecondaryReason, meta) => ({ declineSecondaryReason, meta }),
  );

export function selectDeclineSecondaryReasonEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllDeclineSecondaryReasonEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, DeclineSecondaryReasonRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectDeclineSecondaryReasonEntitiesByName(name: string) {
  return createSelector(selectAllDeclineSecondaryReasonEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectDeclineSecondaryReasonEntitiesByDeclineReasonId(declineReasonId: string) {
  return createSelector(selectAllDeclineSecondaryReasonEntities, (entities) =>
    entities.filter((e) => e.declineReasonId === declineReasonId).valueSeq(),
  );
}

export function selectDeclineSecondaryReasonEntitiesByCancellationReasonId(
  cancellationReasonId: string,
) {
  return createSelector(selectAllDeclineSecondaryReasonEntities, (entities) =>
    entities.filter((e) => e.cancellationReasonId === cancellationReasonId).valueSeq(),
  );
}

export function selectDeclineSecondaryReasonEntitiesByCancellationSecondaryReasonId(
  cancellationSecondaryReasonId: string,
) {
  return createSelector(selectAllDeclineSecondaryReasonEntities, (entities) =>
    entities
      .filter((e) => e.cancellationSecondaryReasonId === cancellationSecondaryReasonId)
      .valueSeq(),
  );
}
//#endregion Selectors
